import { InputSelectionListProps } from '@dixa/lib-shared-habla';
import { SearchParameters, SearchQueue } from 'lib-search-types';
import { HookFormFieldWrappedBaseProps } from 'lib-shared-hook-form';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { FieldPath, FieldValues, useWatch } from 'react-hook-form';
import { useUpdate } from 'react-use';

import { searchApi } from 'apis/search';
import {
  HookFormSearchSelect,
  HookFormSearchSelectProps,
} from 'components/hookForm/hookFormSearchSelect';
import { useAppDispatch } from 'utils/redux/useAppDispatch';

type HookFormQueuesSelectProps = Partial<
  Omit<InputSelectionListProps<string>, 'children'>
>;

/**
 * A wrapper around {@link HookFormSearchSelect} for searching and selecting
 * queues. Provides queue-specific rendering and sensible defaults, such as only
 * showing non-archived queues sorted in alphabetical order.
 *
 * The value is stored as an array of queue IDs. If in single select mode, use
 * selectedValues[0] to get the selected queue ID. Pre-selected queue IDs that
 * are not in the current search results are enriched with a separate API call
 * to get their names for display.
 */
export const HookFormQueuesSelect = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  componentProps = {} as HookFormQueuesSelectProps,
  initialArg,
  lazy,
  ...listProps
}: HookFormFieldWrappedBaseProps<
  TFieldValues,
  TName,
  HookFormQueuesSelectProps
> &
  Pick<
    HookFormSearchSelectProps<'queues', TFieldValues, TName>,
    'initialArg' | 'lazy'
  >): JSX.Element => {
  const dispatch = useAppDispatch();

  /**
   * Cache of queue objects keyed by ID. Populated via two paths:
   *
   * 1. The enrichment effect below (for pre-selected IDs on mount).
   * 2. The transformValue callback (for queues selected from the dropdown). Used
   *    by getItemTitleByValue to display queue names for selected IDs.
   */
  const queueCacheRef = useRef<Record<string, SearchQueue>>({});
  const forceRender = useUpdate();

  const value: string[] | undefined = useWatch({
    control: listProps.control,
    name: listProps.name,
  });

  const valueKey = JSON.stringify(value ?? []);

  // Enrichment effect: when the form has pre-selected queue IDs (e.g. on
  // mount or external value changes), fetch the full queue objects so
  // getItemTitleByValue can display their names. Only fetches IDs not
  // already in the cache, using a single batched search request. Triggers
  // a re-render after populating the cache so the UI picks up the names.
  useEffect(() => {
    const enrichIds = async () => {
      if (!value?.length) return;

      const uncachedIds = value.filter((id) => !queueCacheRef.current[id]);
      if (!uncachedIds.length) return;

      const request = dispatch(
        searchApi.endpoints.readSearchQueues.initiate({
          scope: 'queues',
          filters: {
            strategy: 'all',
            filters: [
              {
                field: 'id',
                operator: 'is_one',
                values: uncachedIds.map((id) => ({ value: id })),
              },
            ],
          },
        }),
      );

      const { data } = await request;
      request.unsubscribe();

      if (!data?.data.results?.length) return;

      for (const { result } of data.data.results) {
        queueCacheRef.current[result.id] = result;
      }

      forceRender();
    };

    enrichIds();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valueKey]);

  const transformValue = useCallback((queue: SearchQueue) => {
    queueCacheRef.current[queue.id] = queue;
    return queue.id;
  }, []);

  const mergedInitialArg = useMemo(
    (): SearchParameters<'queues'> => ({
      scope: 'queues',
      sortBy: { field: 'name', order: 'asc' },
      filters: {
        strategy: 'all',
        filters: [
          {
            field: 'is_archived',
            operator: 'not_equals',
            values: [{ value: 'true' }],
          },
        ],
      },
      ...initialArg,
    }),
    [initialArg],
  );

  return (
    <HookFormSearchSelect
      {...listProps}
      scope="queues"
      initialArg={mergedInitialArg}
      lazy={lazy}
      transformValue={transformValue}
      renderSelectionListItemContent={(queue) => queue.name}
      componentProps={{
        placeholder: 'Select a queue',
        equalityFn: (a: string, b: string) => a === b,
        getItemTitleByValue: (id) =>
          id ? (queueCacheRef.current[id]?.name ?? id) : '',
        ...componentProps,
      }}
    />
  );
};
